import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ApiURL from '../Api/Api';
import {
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  UserIcon,
  CurrencyRupeeIcon,
  CalendarIcon,
  EnvelopeIcon,
  PhoneIcon,
  KeyIcon,
  UserCircleIcon,
  BriefcaseIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  RocketLaunchIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import logo from '../images/logo1.png'; // Adjust path as needed
import { DEFAULT_COMPANY_THEME, compressLogoFile } from '../utils/companyTheme';

const CompanySignup = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponInfo, setCouponInfo] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    owner_name: '',
    owner_email: '',
    owner_phone: '',
    user_name: '',
    password: '',
    confirm_password: '',
    theme_color: DEFAULT_COMPANY_THEME,
    logo_data_url: '',
  });
  const [logoName, setLogoName] = useState('');

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch(`${ApiURL}/get_active_packages.php`);
        const data = await response.json();
        if (data.status === 'success') {
          setPackages(data.packages || []);
        } else {
          setError(data.message || 'Failed to load packages');
        }
      } catch (fetchError) {
        setError(fetchError.message || 'Failed to load packages');
      }
    };
    fetchPackages();
  }, []);

  const selectedFeatureList = useMemo(() => selectedPackage?.feature_list || [], [selectedPackage]);
  const payableAmount = couponInfo
    ? Number(couponInfo.final_amount || 0)
    : Number(selectedPackage?.price || 0);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = async (event) => {
    const file = event.target.files?.[0];
    setError('');
    try {
      const compressed = await compressLogoFile(file);
      setFormData((prev) => ({ ...prev, logo_data_url: compressed }));
      setLogoName(file.name);
    } catch (logoError) {
      setFormData((prev) => ({ ...prev, logo_data_url: '' }));
      setLogoName('');
      setError(logoError.message || 'Unable to process company logo');
    }
  };

  const validateStepTwo = () => {
    if (!selectedPackage) return 'Please select a package first';
    if (!formData.company_name.trim()) return 'Company name is required';
    if (!formData.logo_data_url) return 'Company logo is required';
    if (!/^#[0-9a-f]{6}$/i.test(formData.theme_color)) return 'Please select a company theme color';
    if (!formData.owner_name.trim()) return 'Owner name is required';
    if (!formData.owner_email.trim()) return 'Owner email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.owner_email)) return 'Enter a valid email address';
    if (!/^\d{10}$/.test(formData.owner_phone)) return 'Phone number must be 10 digits';
    if (!formData.user_name.trim()) return 'Username is required';
    if (formData.password.length < 6) return 'Password must be at least 6 characters';
    if (formData.password !== formData.confirm_password) return 'Passwords do not match';
    return '';
  };

  const submitSignup = async (event) => {
    event.preventDefault();
    const validationError = validateStepTwo();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setSuccess('');
    setIsSubmitting(true);
    try {
      const response = await fetch(`${ApiURL}/company_signup.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          package_id: selectedPackage.id,
          coupon_code: couponInfo?.coupon_code || couponCode.trim(),
          theme_color: formData.theme_color,
          logo_data_url: formData.logo_data_url,
        }),
      });
      const result = await response.json();
      if (result.status !== 'success') {
        throw new Error(result.message || 'Signup failed');
      }
      setSuccess(`Account created successfully. Your plan is active until ${new Date(result.subscription_end_date).toLocaleDateString('en-IN')}. Redirecting to login...`);
      setTimeout(() => navigate('/'), 1800);
    } catch (submitError) {
      setError(submitError.message || 'Signup failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const applyCoupon = async () => {
    if (!selectedPackage) {
      setError('Please select a package first');
      return;
    }
    if (!couponCode.trim()) {
      setError('Enter a coupon code');
      return;
    }

    setError('');
    setCouponInfo(null);
    setIsApplyingCoupon(true);
    try {
      const response = await fetch(`${ApiURL}/validate_coupon.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coupon_code: couponCode,
          package_id: selectedPackage.id,
        }),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Coupon could not be applied');
      }
      setCouponInfo(data.coupon);
      setCouponCode(data.coupon?.coupon_code || couponCode.trim().toUpperCase());
    } catch (couponError) {
      setError(couponError.message || 'Coupon could not be applied');
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  // Animation variants
  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 font-['Inter',system-ui,-apple-system,sans-serif]">
      {/* Decorative blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 -left-40 w-96 h-96 bg-indigo-100/40 rounded-full blur-3xl" />
        <div className="absolute top-1/3 left-1/2 w-64 h-64 bg-cyan-100/20 rounded-full blur-3xl" />
      </div>

      {/* Header / Navigation */}
      <header className="relative z-10 border-b border-slate-200/60 bg-white/70 backdrop-blur-md sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4 lg:px-8 flex items-center justify-between">
          {/* Logo and Brand - Desktop */}
          <div className="hidden lg:flex items-center gap-4">
            <img src={logo} alt="Company Logo" className="h-16 w-auto" />
            <div className="inline-flex items-center gap-2 bg-blue-50/80 backdrop-blur-sm rounded-full px-4 py-2 border border-blue-100 shadow-sm">
              <SparklesIcon className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-semibold text-blue-700 tracking-wide">TRANSACTION PLATFORM</span>
            </div>
          </div>
          
          {/* Logo and Brand - Mobile */}
          <div className="flex lg:hidden items-center gap-3">
            <img src={logo} alt="Company Logo" className="h-12 w-auto" />
            <div className="flex items-center gap-1.5">
              <SparklesIcon className="w-3 h-3 text-blue-600" />
              <span className="text-[10px] font-semibold text-blue-700 tracking-wide">TRANSACTION PLATFORM</span>
            </div>
          </div>
          
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white/80 px-5 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300 hover:shadow-md"
          >
            <span>Sign in</span>
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="relative z-10">
        {/* Hero Section with Image Overlay */}
        <section className="relative overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1600&q=80')",
              filter: "brightness(0.95) contrast(1.05)"
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-blue-50/80" />
          
          <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24 lg:px-8">
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            >
              <motion.div variants={fadeUp}>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-tight">
                  Scale your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-600">business operations</span> with precision
                </h1>
                <p className="mt-5 text-lg text-slate-600 max-w-lg leading-relaxed">
                  Empower your team with hierarchical fund management. Company owners, admins, and workers collaborate seamlessly with real-time ledger tracking.
                </p>
                
                <div className="mt-8 flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 bg-white rounded-xl shadow-sm border border-slate-100 px-4 py-2">
                    <ShieldCheckIcon className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm font-medium text-slate-700">Secure by design</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white rounded-xl shadow-sm border border-slate-100 px-4 py-2">
                    <ChartBarIcon className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-slate-700">Real-time ledger</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white rounded-xl shadow-sm border border-slate-100 px-4 py-2">
                    <RocketLaunchIcon className="w-5 h-5 text-indigo-600" />
                    <span className="text-sm font-medium text-slate-700">Instant setup</span>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/50 shadow-xl p-6 lg:p-8">
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                    <UserCircleIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-blue-600 font-semibold uppercase tracking-wider">Onboarding Journey</div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className={`text-sm font-medium ${currentStep === 1 ? 'text-blue-700' : 'text-slate-500'}`}>Select Plan</span>
                      <ArrowRightIcon className="w-3 h-3 text-slate-400" />
                      <span className={`text-sm font-medium ${currentStep === 2 ? 'text-blue-700' : 'text-slate-500'}`}>Create Account</span>
                    </div>
                  </div>
                </div>
                <div className="mt-5 space-y-2">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold mt-0.5">1</div>
                    <p className="text-sm text-slate-600">Choose a package that fits your team size (admins & workers)</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold mt-0.5">2</div>
                    <p className="text-sm text-slate-600">Enter company details and owner credentials</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold mt-0.5">3</div>
                    <p className="text-sm text-slate-600">Start inviting admins & workers, allocate funds instantly</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Packages Selection Section */}
        <section className="py-20 lg:py-28 bg-white/60 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="inline-flex items-center gap-2 bg-blue-50 rounded-full px-4 py-1.5 mb-4">
                <StarIcon className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-semibold text-blue-700">TRANSPARENT PRICING</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Choose your software plan</h2>
              <p className="mt-4 text-slate-500">Scale as you grow. Each plan defines admin and worker limits enforced across your organization.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {packages.map((pkg, idx) => {
                const isSelected = selectedPackage?.id === pkg.id;
                return (
                  <motion.div
                    key={pkg.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    whileHover={{ y: -8 }}
                    onClick={() => {
                      setSelectedPackage(pkg);
                      setCurrentStep(1);
                      setError('');
                      setCouponCode('');
                      setCouponInfo(null);
                    }}
                    className={`relative cursor-pointer rounded-2xl transition-all duration-300 ${
                      isSelected 
                        ? 'ring-2 ring-blue-500 shadow-xl scale-[1.02] bg-white' 
                        : 'bg-white/80 hover:shadow-lg border border-slate-200/80 hover:border-blue-200'
                    } p-6 backdrop-blur-sm`}
                  >
                    {isSelected && (
                      <div className="absolute -top-3 left-6 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                        Selected
                      </div>
                    )}
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold text-slate-800">{pkg.package_name}</h3>
                        <div className="mt-1 text-xs text-blue-600 uppercase tracking-wider">{pkg.status || 'ACTIVE'}</div>
                      </div>
                      {isSelected && <CheckCircleIcon className="w-6 h-6 text-blue-600" />}
                    </div>
                    <div className="mt-4 flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-slate-900">₹{Number(pkg.price || 0).toLocaleString('en-IN')}</span>
                      <span className="text-slate-500 text-sm">/ {pkg.duration_months} months</span>
                    </div>
                    {(pkg.public_coupons || []).length > 0 && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedPackage(pkg);
                          setCouponCode(pkg.public_coupons[0].coupon_code || '');
                          setCouponInfo(null);
                          setCurrentStep(2);
                          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                        }}
                        className="mt-3 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200"
                      >
                        Use code {pkg.public_coupons[0].coupon_code}
                      </button>
                    )}
                    
                    <div className="mt-6 grid grid-cols-3 gap-2">
                      <div className="bg-slate-50 rounded-xl p-2 text-center">
                        <UserGroupIcon className="w-4 h-4 text-blue-500 mx-auto" />
                        <div className="text-xs text-slate-500 mt-1">Users</div>
                        <div className="font-bold text-slate-800">{pkg.max_users}</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-2 text-center">
                        <UserIcon className="w-4 h-4 text-indigo-500 mx-auto" />
                        <div className="text-xs text-slate-500 mt-1">Admins</div>
                        <div className="font-bold text-slate-800">{pkg.max_admins}</div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-2 text-center">
                        <BriefcaseIcon className="w-4 h-4 text-emerald-500 mx-auto" />
                        <div className="text-xs text-slate-500 mt-1">Workers</div>
                        <div className="font-bold text-slate-800">{pkg.max_workers}</div>
                      </div>
                    </div>

                    <div className="mt-6 space-y-2">
                      {(pkg.feature_list || []).slice(0, 3).map((feature) => (
                        <div key={feature} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircleIcon className="w-4 h-4 text-blue-500" />
                          <span>{feature}</span>
                        </div>
                      ))}
                      {(pkg.feature_list || []).length > 3 && (
                        <div className="text-xs text-slate-400 pl-6">+{pkg.feature_list.length - 3} more features</div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {packages.length === 0 && !error && (
              <div className="text-center py-12 text-slate-500">Loading packages...</div>
            )}

            <div className="flex justify-center mt-12">
              <button
                onClick={() => {
                  if (!selectedPackage) {
                    setError('Please select a package first');
                    return;
                  }
                  setCurrentStep(2);
                  setError('');
                  window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                }}
                disabled={!selectedPackage}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-700 to-indigo-700 text-white font-semibold px-8 py-3.5 rounded-xl shadow-md hover:shadow-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue with selected package
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>

        {/* Signup Form Section - Step 2 */}
        <AnimatePresence mode="wait">
          {currentStep === 2 && (
            <motion.section
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="py-20 bg-gradient-to-b from-slate-50 to-white"
            >
              <div className="max-w-6xl mx-auto px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                  {/* Selected Package Summary */}
                  <div className="lg:col-span-2">
                    <div className="sticky top-28 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                        <h3 className="text-white font-semibold">Your Selected Plan</h3>
                      </div>
                      {selectedPackage ? (
                        <div className="p-6">
                          <h4 className="text-xl font-bold text-slate-800">{selectedPackage.package_name}</h4>
                          <div className="mt-2 flex items-baseline gap-1">
                            <span className="text-2xl font-bold">Rs. {payableAmount.toLocaleString('en-IN')}</span>
                            <span className="text-slate-500 text-sm">/{selectedPackage.duration_months} months</span>
                          </div>
                          {couponInfo && (
                            <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                              <div className="flex justify-between gap-3">
                                <span>Coupon {couponInfo.coupon_code} applied</span>
                                <button type="button" onClick={() => setCouponInfo(null)} className="font-semibold text-emerald-700 hover:text-emerald-900">
                                  Remove
                                </button>
                              </div>
                              <div className="mt-2 space-y-1 text-xs">
                                <div className="flex justify-between">
                                  <span>Original price</span>
                                  <span>Rs. {Number(couponInfo.original_amount || 0).toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Discount</span>
                                  <span>- Rs. {Number(couponInfo.discount_amount || 0).toLocaleString('en-IN')}</span>
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="mt-4 space-y-2 text-sm">
                            <div className="flex justify-between py-2 border-b border-slate-100">
                              <span className="text-slate-500">Max Admins</span>
                              <span className="font-semibold">{selectedPackage.max_admins}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b border-slate-100">
                              <span className="text-slate-500">Max Workers</span>
                              <span className="font-semibold">{selectedPackage.max_workers}</span>
                            </div>
                            <div className="flex justify-between py-2">
                              <span className="text-slate-500">Total Users</span>
                              <span className="font-semibold">{selectedPackage.max_users}</span>
                            </div>
                          </div>
                          <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Coupon code</label>
                            <div className="mt-2 flex gap-2">
                              <input
                                value={couponCode}
                                onChange={(event) => {
                                  setCouponCode(event.target.value.toUpperCase());
                                  setCouponInfo(null);
                                }}
                                placeholder="SUMMER50"
                                className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                              />
                              <button
                                type="button"
                                onClick={applyCoupon}
                                disabled={isApplyingCoupon || !couponCode.trim()}
                                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:opacity-50"
                              >
                                {isApplyingCoupon ? 'Checking...' : 'Apply'}
                              </button>
                            </div>
                          </div>
                          <div className="mt-4 text-xs text-slate-500 space-y-1">
                            {selectedFeatureList.map((feature) => (
                              <div key={feature} className="flex gap-2">
                                <span className="text-blue-500">✓</span>
                                <span>{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="p-6 text-slate-500">No package selected</div>
                      )}
                    </div>
                  </div>

                  {/* Signup Form */}
                  <div className="lg:col-span-3">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
                      <div className="mb-6">
                        <h2 className="text-2xl font-bold text-slate-800">Create company account</h2>
                        <p className="text-slate-500 mt-1">Fill in the details to set up your organization workspace</p>
                      </div>

                      {error && (
                        <div className="mb-6 rounded-xl bg-red-50 border border-red-200 p-4 text-red-700 text-sm flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs">!</div>
                          <span>{error}</span>
                        </div>
                      )}
                      {success && (
                        <div className="mb-6 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-700 text-sm flex items-start gap-3">
                          <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
                          <span>{success}</span>
                        </div>
                      )}

                      <form onSubmit={submitSignup} className="space-y-5">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Company name</label>
                          <input
                            name="company_name"
                            value={formData.company_name}
                            onChange={handleFieldChange}
                            placeholder="e.g., Acme Corp"
                            className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-5">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Company logo</label>
                            <label className="flex min-h-[92px] cursor-pointer items-center gap-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 transition hover:border-blue-300 hover:bg-white">
                              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleLogoChange} />
                              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white">
                                {formData.logo_data_url ? (
                                  <img src={formData.logo_data_url} alt="Company Logo Preview" className="h-full w-full object-contain p-1" />
                                ) : (
                                  <BuildingOfficeIcon className="h-6 w-6 text-slate-400" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-slate-800">{logoName || 'Upload company logo'}</p>
                                <p className="mt-1 text-xs text-slate-500">PNG, JPG, or WEBP. It will be compressed automatically.</p>
                              </div>
                            </label>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Theme color</label>
                            <div className="rounded-xl border border-slate-200 bg-white p-3">
                              <div className="flex items-center gap-3">
                                <input
                                  name="theme_color"
                                  type="color"
                                  value={formData.theme_color}
                                  onChange={handleFieldChange}
                                  className="h-12 w-14 cursor-pointer rounded-lg border border-slate-200 bg-white p-1"
                                />
                                <div>
                                  <p className="text-sm font-bold text-slate-800">{formData.theme_color.toUpperCase()}</p>
                                  <p className="text-xs text-slate-500">Navbar, sidebar and buttons</p>
                                </div>
                              </div>
                              <div className="mt-3 flex gap-2">
                                {['#0f66bb', '#059669', '#7c3aed', '#dc2626', '#d97706'].map((color) => (
                                  <button
                                    type="button"
                                    key={color}
                                    onClick={() => setFormData((prev) => ({ ...prev, theme_color: color }))}
                                    className={`h-7 w-7 rounded-full border-2 ${formData.theme_color === color ? 'border-slate-900' : 'border-white'} shadow-sm`}
                                    style={{ backgroundColor: color }}
                                    aria-label={`Select ${color}`}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Owner name</label>
                            <input name="owner_name" value={formData.owner_name} onChange={handleFieldChange} placeholder="Full name" className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Owner username</label>
                            <input name="user_name" value={formData.user_name} onChange={handleFieldChange} placeholder="username" className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Email address</label>
                            <div className="relative">
                              <EnvelopeIcon className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                              <input name="owner_email" type="email" value={formData.owner_email} onChange={handleFieldChange} placeholder="owner@company.com" className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Phone number</label>
                            <div className="relative">
                              <PhoneIcon className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                              <input name="owner_phone" value={formData.owner_phone} onChange={handleFieldChange} placeholder="9876543210" className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition" />
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                            <div className="relative">
                              <KeyIcon className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                              <input name="password" type="password" value={formData.password} onChange={handleFieldChange} placeholder="••••••" className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition" />
                            </div>
                            <p className="text-xs text-slate-400 mt-1">Minimum 6 characters</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Confirm password</label>
                            <input name="confirm_password" type="password" value={formData.confirm_password} onChange={handleFieldChange} placeholder="confirm password" className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition" />
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 pt-4">
                          <button
                            type="button"
                            onClick={() => setCurrentStep(1)}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                          >
                            <ArrowLeftIcon className="w-4 h-4" />
                            Back to plans
                          </button>
                          <button
                            type="submit"
                            disabled={!selectedPackage || isSubmitting}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-700 to-indigo-700 px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:shadow-lg disabled:opacity-60"
                          >
                            {isSubmitting ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Creating...
                              </>
                            ) : (
                              <>
                                Create company account
                                <ArrowRightIcon className="w-4 h-4" />
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white/60 backdrop-blur-sm py-8 mt-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center text-sm text-slate-500">
          <p>© 2025 Algo2BotTXN — Secure transaction ledger platform for modern enterprises.</p>
        </div>
      </footer>
    </div>
  );
};

export default CompanySignup;
